<template>
  <div class="bet-share-receipt-wrapper" aria-hidden="true">
    <div ref="receipt" class="sr-container" v-if="bet">

      <!-- Subheader: type + badges + date -->
      <div class="sr-subheader">
        <div class="sr-subheader-left">
          <img class="logo_skin" :src="logoSrc"/>
        </div>
        <div class="sr-subheader-right">
          <span class="sr-type">{{ $t(bet.type) }}</span>
          <span class="sr-date">{{ new Date(bet.created_at).toLocaleString() }}</span>
        </div>
      </div>

      <!-- Ticket -->
      <div class="sr-ticket">

        <!-- Ticket head -->
        <div class="sr-ticket-head">
          <div class="sr-ticket-head-left">
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" style="flex-shrink:0"><path d="M20 12c0-1.1.9-2 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v4c1.1 0 2 .9 2 2s-.9 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2z"/></svg>
            <span>{{ betOdds.length }} {{ $t('bets.events') }}</span>
          </div>
          <span class="sr-result-badge" :class="`sr-result-badge--${betResult}`">{{ $t('bets.' + betResult) }}</span>
        </div>

        <!-- Ticket body -->
        <div class="sr-ticket-body">

          <!-- Legs -->
          <template v-for="(group, gIdx) in groupedBetOdds">

            <!-- BetBuilder group -->
            <div v-if="group.is_bet_builder" :key="'bb-'+gIdx" class="sr-leg sr-leg--bb">
              <div class="sr-leg-summary">
                <div class="sr-leg-event">
                  <BetBuilderBadge default-text="BB" class="sr-bb-badge" />
                  <span class="sr-leg-event-name">{{ group.odds[0].event_description || (group.odds[0].tournament && group.odds[0].tournament.name) }}</span>
                </div>
              </div>
              <div class="sr-leg-detail">
                <div class="sr-leg-meta">
                  <span class="sr-leg-cat">{{ group.odds[0].category && group.odds[0].category.name }} › {{ group.odds[0].tournament && group.odds[0].tournament.name }}</span>
                  <span class="sr-leg-time">
                    {{ new Date(group.odds[0].time).toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric' }) }}
                    {{ new Date(group.odds[0].time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
                  </span>
                </div>
                <div class="sr-bb-timeline">
                  <div
                    v-for="(odd, idx) in group.odds"
                    :key="odd.unique_id"
                    class="sr-bb-row"
                    :class="{ 'sr-bb-row--last': idx === group.odds.length - 1 }"
                  >
                    <div class="sr-bb-dot"></div>
                    <div class="sr-bb-content">
                      <span class="sr-bb-outcome">{{ getOutcomeName(odd) }}</span>
                      <span class="sr-bb-market">{{ odd.market_alias || odd.market_name }}</span>
                    </div>
                    <div class="sr-bb-result-icon">
                      <svg v-if="odd.result === undefined || odd.result === null" class="result-svg result-svg--pending" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="8"/><polyline points="10 6 10 10 13 12"/></svg>
                      <svg v-else-if="odd.void" class="result-svg result-svg--void" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                      <svg v-else-if="odd.cancelled" class="result-svg result-svg--cancelled" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                      <svg v-else-if="odd.result === true || odd.result === 'win'" class="result-svg result-svg--win" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                      <svg v-else class="result-svg result-svg--lose" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>
                    </div>
                  </div>
                  <div class="sr-bb-combo-row">
                    <span class="sr-leg-odd sr-bb-combined-odd">{{ getBBCombinedOdd(group) | odds_type_filter }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Leg normale -->
            <div v-else :key="'leg-'+gIdx" class="sr-leg">
              <div class="sr-leg-summary">
                <div class="sr-leg-event">
                  <span v-if="group.odds[0].show_time && group.odds[0].time_actual >= 0 && !group.odds[0].match_result" class="sr-badge-odd-live">LIVE</span>
                  <span class="sr-leg-event-name">{{ group.odds[0].event_description || (group.odds[0].tournament && group.odds[0].tournament.name) }}</span>
                </div>
                <div class="sr-leg-result-icon">
                  <svg v-if="group.odds[0].result === undefined" class="sr-result-svg sr-result-svg--pending" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><circle cx="10" cy="10" r="8"/><polyline points="10 6 10 10 13 12"/></svg>
                  <svg v-else-if="group.odds[0].void || group.odds[0].cancelled" class="sr-result-svg sr-result-svg--void" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                  <svg v-else-if="group.odds[0].result" class="sr-result-svg sr-result-svg--win" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                  <svg v-else class="sr-result-svg sr-result-svg--lose" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>
                </div>
              </div>
              <div class="sr-leg-detail">
                <div class="sr-leg-meta">
                  <span class="sr-leg-cat">{{ group.odds[0].category && group.odds[0].category.name }} › {{ group.odds[0].tournament && group.odds[0].tournament.name }}</span>
                  <span class="sr-leg-time">
                    {{ new Date(group.odds[0].time).toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric' }) }}
                    {{ new Date(group.odds[0].time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
                  </span>
                </div>
                <div class="sr-leg-market-row">
                  <span class="sr-leg-outcome">{{ getOutcomeName(group.odds[0]) }}</span>
                  <span class="sr-leg-odd">{{ group.odds[0].outcome_odd_value | odds_type_filter }}</span>
                </div>
                <div class="sr-leg-market-name">{{ group.odds[0].market_alias || group.odds[0].market_name }}<template v-if="getOddLine(group.odds[0])"> {{ getOddLine(group.odds[0]) }}</template></div>
              </div>
            </div>

          </template>



          <!-- Footer stats -->
          <div class="sr-footer">
            <div class="sr-footer-row l">
              <span class="sr-footer-label">{{ $t('amount') }}</span>
              <span class="sr-footer-value">{{ currencySymbol }} {{ betAmount | round_digits(2) }}</span>
            </div>
            <div class="sr-footer-row r">
              <span class="sr-footer-label">{{ betResult !== 'pending' ? $t('winning') : $t('potential') + ' ' + $t('winning') }}</span>
              <span class="sr-footer-value" :class="betResult !== 'pending' ? 'sr-footer-value--win' : 'sr-footer-value--accent'">
                {{ currencySymbol }} {{ betWinning | round_digits(2) }}
              </span>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';
import {mapGetters} from "vuex";
import { toDataUrl } from "@/services/imageDataUrl";
import BetBuilderBadge from '@/components/badges/BetBuilderBadge.vue';

export default {
  name: 'BetShareReceipt',
  components: { BetBuilderBadge },

  props: {
    bet: {
      type: Object,
      default: null,
    },
    currencySymbol: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      logoDataUrl: null,
    };
  },

  watch: {
    getSkinLogoUrl: {
      immediate: true,
      handler(url) {
        if (url) this._preloadLogo(url);
      },
    },
  },

  computed: {
    ...mapGetters({
      'getSkinLogoUrl': 'settings/getSkinLogoUrl'
    }),
    logoSrc() {
      return this.logoDataUrl || this.getSkinLogoUrl;
    },
    betAmount() {
      if (!this.bet) return 0;
      return this.bet.amount ?? this.bet.amount_real ?? 0;
    },
    betWinning() {
      if (!this.bet) return 0;
      return this.bet.winning_total ?? this.bet.winning_real ?? 0;
    },
    betOdds() {
      if (!this.bet) return [];
      return this.bet.odds || [];
    },
    betResult() {
      return this.bet?.result || 'pending';
    },
    groupedBetOdds() {
      const map = new Map();
      for (const odd of this.betOdds) {
        const key = odd.bet_builder_id || odd.unique_id;
        if (!map.has(key)) {
          map.set(key, { key, is_bet_builder: !!odd.bet_builder_id, odds: [] });
        }
        map.get(key).odds.push(odd);
      }
      return Array.from(map.values());
    },
  },

  methods: {
    async _preloadLogo(url) {
      // Memoised per URL in the service: this watcher is `immediate`, so it
      // used to re-issue the request on every re-creation of the receipt — and
      // against a host without CORS headers every one of them failed silently.
      const b64 = await toDataUrl(url);
      if (b64) this.logoDataUrl = b64;
    },

    async triggerShare() {
      if (!this.bet) return;
      this.$emit('share-start');

      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        const node = this.$refs.receipt;
        const logoDataUrl = this.logoDataUrl;
        const canvas = await html2canvas(node, {
          backgroundColor: '#1a1a1a',
          scale: window.devicePixelRatio * 2 || 4,
          useCORS: true,
          logging: false,
          imageTimeout: 0,
          onclone: (clonedDoc) => {
            if (logoDataUrl) {
              const clonedImg = clonedDoc.querySelector('.logo_skin');
              if (clonedImg) clonedImg.src = logoDataUrl;
            }
          },
        });

        const betCode = this.bet.bet_code || this.bet.code || this.bet.betCode || 'bet';
        const fileName = `bet_${betCode}.png`;

        await new Promise((resolve, reject) => {
          canvas.toBlob(async (blob) => {
            if (!blob) { reject(new Error('toBlob failed')); return; }
            try {
              const file = new File([blob], fileName, { type: 'image/png' });
              const canShareFiles = navigator.share && navigator.canShare && navigator.canShare({ files: [file] });
              if (canShareFiles) {
                try {
                  await navigator.share({ files: [file], title: '' });
                } catch (shareErr) {
                  // AbortError = utente ha chiuso il dialogo → niente da fare
                  if (!shareErr || shareErr.name !== 'AbortError') {
                    this._downloadCanvas(canvas, fileName);
                  }
                }
              } else {
                this._downloadCanvas(canvas, fileName);
              }
              resolve();
            } catch (e) {
              reject(e);
            }
          }, 'image/png');
        });

        this.$emit('share-end', { success: true });
      } catch (err) {
        console.error(err);
        this.$emit('share-end', { success: false, error: err });
      }
    },

    _downloadCanvas(canvas, fileName) {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    getOddLine(odd) {
      if (odd.line && odd.line !== '') return odd.line;
      if (odd.baseLine && odd.baseLine !== '') return odd.baseLine;
      if (odd.specifiers && odd.specifiers !== '') {
        const part = odd.specifiers.split(' ')[0];
        return isNaN(part.charAt(0)) && part.charAt(0) !== '-' && part.charAt(0) !== '+' ? null : part;
      }
      return null;
    },

    getBBCombinedOdd(group) {
      const main = group.odds.find(o => parseFloat(o.outcome_odd_value) !== 1);
      return main ? main.outcome_odd_value : (group.odds[0] ? group.odds[0].outcome_odd_value : 0);
    },
    getOutcomeName(odd) {
      const name = odd.outcome_name;
      if (!name) return odd.outcome_alias || '';
      if (name === 'X') return odd.outcome_alias || odd.outcome_name;
      const lower = name.toLowerCase();
      const c = odd.competitors;
      if (lower === 'local' && c && c[0]) return c[0].name;
      if (lower === 'visitante' && c && c[1]) return c[1].name;
      if ((name === '1' || name === '2') && odd.unique_id !== '128' && c) {
        const found = c.find(t => t.position === name);
        if (found) return found.name;
      }
      return odd.outcome_alias || name;
    },
  },
};
</script>

<style scoped>
/* Nascosto fuori schermo — solo per html2canvas */
.bet-share-receipt-wrapper {
  position: fixed;
  left: -9999px;
  top: 0;
  pointer-events: none;
  z-index: -1;
}

.sr-container {
  width: 380px;
  background-color: var(--sidebar);
  padding: 0 0 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0;
  font-family: Arial, Helvetica, sans-serif;
}

/* ── Subheader ── */
.sr-subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 10px;
}
.sr-subheader-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;

}
.sr-subheader-right{
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 1;
}
.logo_skin{
  width: 100%;
}
.sr-type {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  text-transform: capitalize;
  color: var(--text-platform);
}
.sr-date {
  font-size: var(--font-size-xl);
  color: var(--text-op-50-platform);
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: 8px;
}
.sr-badge-live {
  font-size: var(--font-size-xs);
  font-weight: 800;
  background: var(--live);
  color: var(--text-platform);
  padding: 2px 5px;
  border-radius: 3px;
}
.sr-ctx-badge {
  font-size: var(--font-size-xs);
  font-weight: 800;
  background: rgba(33,150,243,0.2);
  color: #2196f3;
  padding: 2px 5px;
  border-radius: 3px;
}
.sr-badge-cashout {
  font-size: var(--font-size-xs);
  font-weight: 700;
  background: rgba(249,168,37,0.15);
  color: #f9a825;
  padding: 2px 6px;
  border-radius: 3px;
}

/* ── Ticket ── */
.sr-ticket {
  margin: 0 12px 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

/* Ticket head */
.sr-ticket-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: rgba(255,255,255,0.05);
  flex-shrink: 0;
  border-radius: 6px;
}
.sr-ticket-head-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-3xl);
  font-weight: 600;
  color: var(--text-platform);
}
.sr-result-badge {
  font-size: var(--font-size-md);
  font-weight: 800;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 4px;
  color: #000;
}
.sr-result-badge--pending { background: var(--pending); color: var(--text-platform) }
.sr-result-badge--win     { background: var(--win); color: var(--text-platform); }
.sr-result-badge--lose    { background: var(--lose); color: var(--text-platform); }
.sr-result-badge--void    { background: #2196f3; color: var(--text-platform); }

/* Ticket body */
.sr-ticket-body { padding: 10px 0; }

/* Legs */
.sr-leg {
  background: rgba(255,255,255,0.04);
  border-radius: 6px;
  margin-bottom: 6px;
  overflow: hidden;
}
.sr-leg-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  gap: 8px;
}
.sr-leg-event {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}
.sr-leg-event-name {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--text-platform);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sr-leg-result-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.sr-badge-odd-live {
  font-size: var(--font-size-xs);
  font-weight: 800;
  background: var(--live);
  color: var(--text-platform);
  padding: 1px 4px;
  border-radius: 3px;
  flex-shrink: 0;
}
.sr-result-svg--pending { stroke: var(--text-op-50-platform, rgba(255,255,255,0.4)); fill: none; }

/* ── BetBuilder ── */
.sr-bb-badge { flex-shrink: 0; padding: 2px 5px; border-radius: 4px; font-size: var(--font-size-xs); font-weight: 800; line-height: 13px; color: var(--bg-body); background: var(--color-primary-light); }
.sr-bb-timeline { display: flex; flex-direction: column; gap: 8px; padding: 6px 0 4px 16px; position: relative; margin-top: 6px; }
.sr-bb-row {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1px;
}
.sr-bb-row:not(.sr-bb-row--last)::before { content: ''; position: absolute; left: -9px; top: 5px; bottom: -8px; width: 1px; background: var(--color-primary-light, #21a8f7); opacity: 0.4; }
.sr-bb-dot { position: absolute; left: -12px; top: 4px; width: 6px; height: 6px; border-radius: 50%; background: var(--color-primary-light, #21a8f7); box-shadow: 0 0 4px rgba(33,168,247,0.6); z-index: 1; }
.sr-bb-content { display: flex; flex-direction: column; gap: 1px; }
.sr-bb-outcome { font-size: var(--font-size-xl); font-weight: 600; color: var(--text-platform); }
.sr-bb-market { font-size: var(--font-size-md); color: var(--text-op-50-platform); text-transform: uppercase; }
.sr-bb-combo-row { display: flex; justify-content: flex-end; margin-top: 4px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.07); }
.sr-bb-combined-odd { color: var(--color-primary-light) !important; }
.sr-result-svg--win  { color: var(--win); }
.sr-result-svg--lose { color: var(--lose); }
.sr-result-svg--void { color: #2196f3; }

.sr-leg-detail {
  padding: 0 10px 8px;
  border-top: 1px solid rgba(255,255,255,0.06);
  padding-top: 6px;
}
.sr-leg-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}
.sr-leg-cat {
  font-size: var(--font-size-md);
  color: var(--text-op-50-platform);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}
.sr-leg-time {
  font-size: var(--font-size-md);
  color: var(--text-op-50-platform);
  flex-shrink: 0;
  margin-left: 8px;
}
.sr-leg-market-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}
.sr-leg-outcome {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--text-platform);
}
.sr-leg-odd {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--text-platform);
  flex-shrink: 0;
}
.sr-leg-market-name {
  font-size: var(--font-size-md);
  color: var(--text-op-50-platform);
}

/* Divider stile biglietto strappato */
.sr-ticket-divider {
  margin: 12px -14px;
  display: flex;
  align-items: center;
}
.sr-ticket-divider-notch {
  width: 10px;
  height: 20px;
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  flex-shrink: 0;
}
.sr-ticket-divider-notch--left  { border-radius: 0 10px 10px 0; border-left: none; }
.sr-ticket-divider-notch--right { border-radius: 10px 0 0 10px; border-right: none; }
.sr-ticket-divider-line {
  flex: 1;
  border-top: 2px dashed rgba(255,255,255,0.1);
}

/* Footer stats */
.sr-footer { padding-top: 4px; display: flex;  width: 100%; justify-content: space-between; }
.sr-footer-row {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}
.sr-footer-row.l{
  align-items: flex-start;
}
.sr-footer-row.r{
  align-items: flex-end;
}
.sr-footer-label {
  font-size: var(--font-size-xl);
  color: var(--text-op-50-platform);
}
.sr-footer-value {
  font-size: var(--font-size-6xl);
  font-weight: 700;
  color: var(--text-platform);
}
.sr-footer-value--accent { color: var(--color-primary-light); }
.sr-footer-value--win    { color: var(--color-primary); font-size: var(--font-size-4xl); }
.result-svg { width: 16px; height: 16px; flex-shrink: 0; }
.result-svg--win      { fill: var(--win); }
.result-svg--lose     { fill: var(--lose); }
.result-svg--void     { fill: #2196f3; }
.result-svg--pending  { stroke: var(--text-op-50-platform, rgba(255,255,255,0.4)); fill: none; }
.result-svg--cancelled{ fill: #fff; }

.sr-bb-result-icon{
  display: flex; align-items: center; flex-shrink: 0; margin-left: auto;
}
</style>
